import React, { Fragment } from 'react';
import _debounce from 'lodash/debounce';
import * as PropTypes from 'prop-types';

import { generateRandomKey } from '../../utils';

import styles from './index.module.css';

const propTypes = {
  inputProps: PropTypes.shape({
    className: PropTypes.object,
    id: PropTypes.string,
    content: PropTypes.any,
    start: PropTypes.number,
    distance: PropTypes.number,
    duration: PropTypes.number,
    arrowClass: PropTypes.object,
    hideArrowsInMobile: PropTypes.bool,
  }),
  children: PropTypes.any.isRequired,
};

const defaultProps = {
  inputProps: {
    className: {},
    id: '',
    content: '',
    start: 0,
    distance: 0,
    duration: 0,
    arrowClass: {},
    hideArrowsInMobile: false,
  },
};

class Carousel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      leftArrow: false,
      rightArrow: false,
      scrollWidth: 0,
      dotIndex: 0,
    };
    this.ref = React.createRef();
  }

  componentDidMount() {
    const { inputProps } = this.props;
    const {
      className: { activeItem },
    } = inputProps;
    this.setState({
      scrollWidth: this.ref.current.scrollWidth,
    });
    if (inputProps.start) {
      this.ref.current.scrollLeft = inputProps.start * this.ref.current.clientWidth;
    }
    if (activeItem) {
      this.scrollIntoActiveItem(activeItem);
    }
    setTimeout(() => {
      this.checkArrows();
    }, 300);

    this.props.onItemChange && this.props.onItemChange(this.state.dotIndex);
  }

  componentDidUpdate(prevProps, prevState) {
    const { children } = this.props;
    if (
      prevState.scrollWidth !== this.ref.current.scrollWidth &&
      this.ref.current.scrollWidth !== this.ref.current.clientWidth
    ) {
      const maxScrollLeft = this.ref.current.scrollWidth - this.ref.current.clientWidth;
      this.setState({
        scrollWidth: this.ref.current.scrollWidth,
        leftArrow: this.ref.current.scrollLeft > 10,
        rightArrow: maxScrollLeft > this.ref.current.scrollLeft + 1,
      });
    }
    if (prevProps.children.length !== children.length) {
      this.checkArrows();
    }
    if (prevState.dotIndex !== this.state.dotIndex) {
      this.props.onItemChange && this.props.onItemChange(this.state.dotIndex);
    }
  }

  scrollIntoActiveItem = (activeItemClassName) => {
    const activeItem = document.querySelector(`.carousel-outer-container .${activeItemClassName}`);
    if (!activeItem) {
      return;
    }
    activeItem.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'center',
    });

    const offsetTop = window.scrollY;
    const windowHeigth = window.innerHeight;

    if (offsetTop < windowHeigth) {
      window.scrollTo(0, 0);
    }
  };

  checkArrows = () => {
    const { leftArrow, rightArrow, dotIndex } = this.state;
    if (this.ref && this.ref.current) {
      const maxScrollLeft = this.ref.current.scrollWidth - this.ref.current.clientWidth;
      if (
        (this.ref.current.scrollLeft > 10 && !leftArrow) ||
        (this.ref.current.scrollLeft < 10 && leftArrow) ||
        (maxScrollLeft < this.ref.current.scrollLeft + 5 && rightArrow) ||
        (maxScrollLeft > this.ref.current.scrollLeft + 5 && !rightArrow)
      ) {
        this.setState({
          leftArrow: this.ref.current.scrollLeft > 10,
          rightArrow: maxScrollLeft > this.ref.current.scrollLeft + 5,
        });
      }

      const getWidth = this.ref.current.children[0] ? this.ref.current.children[0].clientWidth : 1;
      const left = this.ref.current.scrollLeft + 10;
      const itemStart = Math.round(left / getWidth);

      if (itemStart !== dotIndex) {
        this.setState({
          dotIndex: itemStart,
        });
      }
    }
  };

  slideContainer = (e, ref, side, distance, duration) => {
    e.preventDefault();
    e.stopPropagation();
    const container = ref.current;
    const containerWidth = container.getBoundingClientRect().width;
    const start = container.scrollLeft;
    const change = side * (distance || containerWidth);
    const startDate = +new Date();

    const easeInOutQuad = (t, b, c, d) => {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    };

    const animateScroll = () => {
      const currentDate = +new Date();
      const currentTime = currentDate - startDate;
      container.scrollLeft = parseInt(easeInOutQuad(currentTime, start, change, duration));
      if (currentTime < duration) {
        requestAnimationFrame(animateScroll);
      }
    };
    animateScroll();
  };

  moveToIndex = (e, index) => {
    const { dotIndex } = this.state;
    const side = index > dotIndex ? 1 : -1;
    const containerMargin = this.ref.current.scrollLeft;
    const computedDistance = (this.ref.current.children[index].offsetLeft - containerMargin) * side;
    this.slideContainer(e, this.ref, side, computedDistance, 600);
  };

  slideContainerDebounsed = _debounce(
    (e, side) => {
      const { inputProps } = this.props;
      const { duration, distance } = inputProps;
      this.slideContainer(e, this.ref, side, distance, duration);
    },
    300,
    {
      leading: true,
      trailing: false,
    }
  );

  triggerSlide = (e, side) => this.slideContainerDebounsed(e, side);

  renderArrows = () => {
    const { leftArrow, rightArrow } = this.state;
    const { inputProps } = this.props;
    const { arrowClass } = inputProps;
    return (
      <>
        {leftArrow && (
          <span className={arrowClass.left} onClick={(e) => this.triggerSlide(e, -1)} />
        )}
        {rightArrow && (
          <span className={arrowClass.right} onClick={(e) => this.triggerSlide(e, 1)} />
        )}
      </>
    );
  };

  render() {
    const { children, inputProps } = this.props;
    const { className, id, content, dotLoop, dots } = inputProps;
    const { dotIndex } = this.state;

    return (
      <Fragment>
        <div className={`carousel-outer-container ${className.outer ? className.outer : ''}`}>
          {content}
          {this.renderArrows()}

          <div className={className.middle}>
            <div id={id} className={className.inner} ref={this.ref} onScroll={this.checkArrows}>
              {children}
            </div>
          </div>
          {dots && (
            <div
              id="testimonial-dots-container"
              className={`${styles['site-testimonials-dots']} container noSelect`}
            >
              {dotLoop.map((data, index) => (
                <div
                  key={generateRandomKey()}
                  className={`${styles['carousel-dot']} ${
                    index === dotIndex ? styles['carousel-dot-active'] : ''
                  }`}
                  onClick={(e) => this.moveToIndex(e, index)}
                />
              ))}
            </div>
          )}
        </div>
      </Fragment>
    );
  }
}

Carousel.propTypes = propTypes;
Carousel.defaultProps = defaultProps;

export default Carousel;
